import { motion } from 'framer-motion';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Row } from '~/commonComponents';
import { removeFavoriteProfessional } from '../../../actions';
import StarIcon from '../../../images/icons/Star';
import { Colors } from '../../../themes/colors';
import ConfirmPopup from '../../shared/ConfirmPopup';
import { IProfessionalCardUser } from '../interfaces';
import { FavoriteHeartButton } from './shared/FavoriteHeartButton';
import { ProfilePictureHover } from './shared/ProfilePictureHover';

interface IProfessionalCardProps {
  user: IProfessionalCardUser;
  Details: React.ComponentType<{ user: IProfessionalCardUser }>;
  Actions: React.ComponentType<{ user: IProfessionalCardUser }>;
  showRemoveIcon?: boolean;
  isNewDesign?: boolean;
  onFavorite?: () => void;
  isFavoriteCard?: boolean;
}

const getBorderColor = (isAvailable: boolean | undefined, isFavorite: boolean | undefined) => {
  if (isAvailable && isFavorite) {
    return Colors.favorites_400;
  }
  if (!isAvailable) {
    return Colors.neutral_300;
  }
  return '';
};

export const ProfessionalCard = ({
  user,
  Details,
  Actions,
  showRemoveIcon,
  isNewDesign,
  onFavorite,
  isFavoriteCard,
}: IProfessionalCardProps) => {
  const dispatch = useDispatch();
  const { isAvailable, isFavorite } = user;
  const borderColor = !isNewDesign ? getBorderColor(isAvailable, isFavorite) : '';
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const handleShowRemoveModal = () => {
    setShowRemoveModal(!showRemoveModal);
  };

  const handleOnFavorite = () => {
    // Add a small delay to allow the heart animation to complete
    setTimeout(() => {
      onFavorite?.();
    }, 500);
  };

  return (
    <motion.div
      data-cy="professional-list-item"
      key={user.id}
      initial={{ opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.3,
        exit: { duration: 0.2 },
        layout: { duration: 0.3 },
      }}
      layout
      style={
        isNewDesign
          ? {
              width: '100%',
              boxSizing: 'border-box',
              minWidth: 0,
              position: 'relative',
              display: 'flex',
              backgroundColor: isFavoriteCard ? Colors.favorites_30 : Colors.neutral_50,
              borderRadius: 8,
              boxShadow:
                isAvailable || isFavoriteCard ? '0px 2px 4px 0px rgba(111, 131, 141, 0.33)' : '',
              alignItems: 'center',
              padding: '18px 0 14px 22px',
              maxHeight: isFavoriteCard ? 104 : undefined,
              minHeight: 80,
            }
          : {
              width: 700,
              height: 98,
              position: 'relative',
              display: 'flex',
              backgroundColor: Colors.neutral_50,
              borderRadius: '16px',
              borderTopLeftRadius: 84,
              borderBottomLeftRadius: 84,
              boxShadow: isAvailable ? '0px 2px 4px 0px rgba(111, 131, 141, 0.33)' : '',
            }
      }
    >
      {isNewDesign ? (
        <ProfilePictureHover
          id={user.id}
          profileImage={user.profileImage}
          isAvailable={isAvailable}
          isFavorite={isFavorite}
        />
      ) : (
        <img
          src={user.profileImage}
          alt="Profile"
          style={{
            filter: isAvailable ? 'none' : 'grayscale(100%)',
            opacity: isAvailable ? 1 : 0.5,
            width: 98,
            height: 98,
            borderRadius: '50%',
            ...(borderColor && {
              border: `3px solid ${borderColor}`,
              width: 92,
              height: 92,
            }),
            boxShadow: '0px 3px 10px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.15)',
          }}
        />
      )}

      {isNewDesign ? (
        <Row
          flex={1}
          paddingRight={onFavorite || isFavoriteCard ? 20 : 5}
          alignItems="center"
          justifyContent="space-between"
          minWidth={0}
        >
          <Details user={user} />
          <Actions user={user} />
        </Row>
      ) : (
        <div
          style={{
            display: 'flex',
            flex: 1,
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Details user={user} />
          <Actions user={user} />
        </div>
      )}

      {onFavorite && isNewDesign && (
        <FavoriteHeartButton onClick={handleOnFavorite} disabled={!isAvailable} />
      )}

      {isFavoriteCard && isNewDesign && (
        <FavoriteHeartButton
          onClick={handleShowRemoveModal}
          isFavorite
          isPendingRemoval={showRemoveModal}
        />
      )}

      {showRemoveIcon && (
        <button
          type="button"
          data-cy="remove-button"
          style={{
            all: 'unset',
            position: 'absolute',
            top: -8,
            right: -8,
            backgroundColor: 'transparent',
            cursor: 'pointer',
          }}
          onClick={handleShowRemoveModal}
        >
          <StarIcon color={Colors.secondary_500} />
        </button>
      )}

      {showRemoveModal && (
        // @ts-expect-error - missing type
        <ConfirmPopup
          title="Removal Confirmation"
          description={
            <>
              Please confirm that you would like to remove <b>{user.fullname}</b> from your
              Favorites List.
            </>
          }
          leftButtonText="Cancel"
          leftButtonAction={() => handleShowRemoveModal()}
          rightButtonText="Remove"
          rightButtonAction={() => {
            dispatch(removeFavoriteProfessional({ userId: user.id }));
            handleShowRemoveModal();
          }}
        />
      )}
    </motion.div>
  );
};
